import React, { useState } from 'react';
import { PRO_MODELS_SUBTITLE } from '../../constants/aiModels';
import './PlanCompareTable.css';

export const PLAN_COLUMNS = [
  { id: 'free', name: '무료', tagline: '먼저 써 보기', cta: '무료로 시작' },
  {
    id: 'pro',
    name: 'PRO',
    tagline: '1명부터 · 개인 또는 학교 예산',
    cta: 'PRO 시작',
    badge: '추천',
    subAction: { id: 'pro-school', label: '학교 예산으로 구매' },
  },
  { id: 'school', name: '학교(엔터프라이즈)', tagline: '12개월 · 선생님 10명 이상', cta: '견적 받기' },
];

// 무료/PRO 값은 백엔드 제한과 같아야 한다 (생기부 main.py FREE 인원, 일괄 SAENGGIBU_BATCH_MAX_STUDENTS, PPT generationLimits, 스타일 FREE_ANALYZE_LIMIT).
// core 줄만 처음에 보이고, 나머지는 "모든 기능 보기"로 펼친다.
export const COMPARE_GROUPS = [
  {
    id: 'tools',
    title: '만들기 AI 도구',
    rows: [
      { label: 'AI 모델', core: true, free: '빠른 모드', pro: PRO_MODELS_SUBTITLE, school: true },
      { label: 'AI PPT', core: true, free: '글자형 · 8장 · 디자인형 1회 체험', pro: '40장 · 디자인형', school: true },
      { label: 'AI 학습지', free: '글자형 · 디자인형 1회 체험', pro: '글자형 + 디자인형', school: true },
      { label: 'AI 한글', free: '글로만 작성', pro: '학교 양식 채우기 · 한글 파일 편집', school: true },
      { label: 'AI 이미지', free: '기본 모델', pro: '고급 모델 · 참조 이미지', school: true },
      { label: '파일 첨부', free: '자료함 글 3개', pro: 'PDF · HWP · 이미지 · Excel', school: true },
      { label: '내 스타일', free: '공통 스타일 · 분석 3회', pro: '종류별 스타일', school: true },
    ],
  },
  {
    id: 'records',
    title: '생기부 · 채점',
    rows: [
      { label: '생기부 생성', core: true, free: '교과 3~4명 · 창체 10명', pro: '한 번에 30명', school: true },
      { label: '반 전체 생기부', core: true, free: false, pro: '한 번에 150명', school: true },
      { label: '서술형 채점', core: true, free: '1명 · 직접 입력', pro: '반 전체 · 스캔 PDF', school: true },
    ],
  },
  {
    id: 'manage',
    title: '학교 관리',
    exclusive: true,
    rows: [
      { label: '학교 관리자 화면', core: true, hint: '초대 링크 · 사용 현황 · 자리 회수', free: false, pro: false, school: true },
      { label: '학교 생기부 검토', core: true, hint: '선생님별 제출 · 같은 문장 점검', free: false, pro: false, school: true },
      { label: '전출 · 휴직 선생님 자리 회수', free: false, pro: false, school: true },
      { label: '도입 연수', free: false, pro: false, school: true },
    ],
  },
  {
    id: 'trust',
    title: '보안 · 서류',
    rows: [
      { label: '학생 기록은 본인만', hint: '관리자는 사용 건수만 봐요', free: true, pro: true, school: true },
      { label: '학교 예산 결제', core: true, hint: '견적서 · 품의서 · 세금계산서', free: false, pro: true, school: true },
    ],
  },
];

export const CORE_ROWS = COMPARE_GROUPS.flatMap((group) => group.rows.filter((row) => row.core));

const PLAN_IDS = PLAN_COLUMNS.map((plan) => plan.id);

const SubAction = ({ plan, onAction }) => (plan.subAction ? (
  <button type="button" className="plan-compare-subaction" onClick={() => onAction(plan.subAction.id)}>
    {plan.subAction.label}
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  </button>
) : null);

const CheckIcon = () => (
  <svg className="plan-compare-check" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg className="plan-compare-cross" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const CompareCell = ({ value }) => {
  if (value === true) {
    return (
      <span className="plan-compare-value is-yes">
        <CheckIcon />
        <span className="plan-compare-sr">있어요</span>
      </span>
    );
  }
  if (value === false || value == null) {
    return (
      <span className="plan-compare-value is-no">
        <CrossIcon />
        <span className="plan-compare-sr">없어요</span>
      </span>
    );
  }
  return <span className="plan-compare-value is-text">{value}</span>;
};

const PlanCompareTable = ({ proPriceLabel, schoolPriceLabel, onAction }) => {
  const [mobilePair, setMobilePair] = useState(['pro', 'school']);
  const [showAll, setShowAll] = useState(false);
  const groups = showAll ? COMPARE_GROUPS : [{ id: 'core', rows: CORE_ROWS }];

  const priceLabels = {
    free: '0원',
    pro: proPriceLabel,
    school: schoolPriceLabel,
  };

  const setPairAt = (index, planId) => {
    setMobilePair((prev) => {
      const next = [...prev];
      const other = next[1 - index];
      next[index] = planId;
      if (planId === other) {
        next[1 - index] = PLAN_IDS.find((id) => id !== planId && id !== prev[index]) || prev[index];
      }
      return next;
    });
  };

  return (
    <div className="plan-compare" data-testid="plan-compare">
      {/* 데스크톱: 3열 표, 열 제목 고정 */}
      <div className="plan-compare-desktop" role="table" aria-label="요금제 비교표">
        <div className="plan-compare-head" role="rowgroup">
          <div className="plan-compare-row plan-compare-head-row" role="row">
            <div className="plan-compare-label-cell" role="columnheader">
              <span className="plan-compare-sr">기능</span>
            </div>
            {PLAN_COLUMNS.map((plan) => (
              <div
                key={plan.id}
                role="columnheader"
                className={`plan-compare-plan-head plan-${plan.id}${plan.badge ? ' is-featured' : ''}`}
              >
                {plan.badge && <span className="plan-compare-badge">{plan.badge}</span>}
                <span className="plan-compare-plan-name">{plan.name}</span>
                <span className="plan-compare-plan-tagline">{plan.tagline}</span>
                <span className="plan-compare-plan-price">{priceLabels[plan.id]}</span>
                <button
                  type="button"
                  className={`plan-compare-cta${plan.badge ? ' is-primary' : ''}`}
                  onClick={() => onAction(plan.id)}
                >
                  {plan.cta}
                </button>
                <SubAction plan={plan} onAction={onAction} />
              </div>
            ))}
          </div>
        </div>
        {groups.map((group) => (
          <div
            key={group.id}
            className={`plan-compare-group${group.exclusive ? ' is-exclusive' : ''}`}
            role="rowgroup"
          >
            {group.title && (
              <div className="plan-compare-group-title" role="row">
                <span role="rowheader">
                  {group.title}
                  {group.exclusive && <span className="plan-compare-group-tag">12개월 · 10명 이상</span>}
                </span>
              </div>
            )}
            {group.rows.map((row) => (
              <div key={row.label} className="plan-compare-row" role="row">
                <div className="plan-compare-label-cell" role="rowheader">
                  <span className="plan-compare-label">{row.label}</span>
                  {row.hint && <span className="plan-compare-hint">{row.hint}</span>}
                </div>
                {PLAN_COLUMNS.map((plan) => (
                  <div key={plan.id} role="cell" className={`plan-compare-cell plan-${plan.id}${plan.badge ? ' is-featured' : ''}`}>
                    <CompareCell value={row[plan.id]} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* 모바일: 두 플랜만 골라 나란히 비교 */}
      <div className="plan-compare-mobile">
        <div className="plan-compare-picker">
          {mobilePair.map((planId, index) => (
            <label key={index} className="plan-compare-picker-field">
              <span className="plan-compare-sr">{index === 0 ? '왼쪽 플랜' : '오른쪽 플랜'}</span>
              <select
                value={planId}
                onChange={(e) => setPairAt(index, e.target.value)}
                aria-label={index === 0 ? '비교할 첫 번째 플랜' : '비교할 두 번째 플랜'}
              >
                {PLAN_COLUMNS.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
            </label>
          ))}
        </div>
        <div className="plan-compare-mobile-heads">
          {mobilePair.map((planId) => {
            const plan = PLAN_COLUMNS.find((p) => p.id === planId);
            return (
              <div key={planId} className={`plan-compare-mobile-head plan-${plan.id}`}>
                <span className="plan-compare-plan-tagline">{plan.tagline}</span>
                <span className="plan-compare-plan-price">{priceLabels[plan.id]}</span>
                <button
                  type="button"
                  className={`plan-compare-cta${plan.badge ? ' is-primary' : ''}`}
                  onClick={() => onAction(plan.id)}
                >
                  {plan.cta}
                </button>
                <SubAction plan={plan} onAction={onAction} />
              </div>
            );
          })}
        </div>
        {groups.map((group) => (
          <div key={group.id} className={`plan-compare-mobile-group${group.exclusive ? ' is-exclusive' : ''}`}>
            {group.title && (
              <div className="plan-compare-group-title">
                {group.title}
                {group.exclusive && <span className="plan-compare-group-tag">12개월 · 10명 이상</span>}
              </div>
            )}
            {group.rows.map((row) => (
              <div key={row.label} className="plan-compare-mobile-row">
                <div className="plan-compare-mobile-label">
                  <span className="plan-compare-label">{row.label}</span>
                  {row.hint && <span className="plan-compare-hint">{row.hint}</span>}
                </div>
                <div className="plan-compare-mobile-values">
                  {mobilePair.map((planId) => (
                    <div key={planId} className={`plan-compare-cell plan-${planId}`}>
                      <CompareCell value={row[planId]} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <button
        type="button"
        className="plan-compare-more"
        aria-expanded={showAll}
        onClick={() => setShowAll((value) => !value)}
      >
        {showAll ? '핵심만 보기' : '모든 기능 보기'}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={showAll ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
        </svg>
      </button>
    </div>
  );
};

export default PlanCompareTable;
